import ErrorPage from "@/components/pages/ErrorPage";
import { useDiaryByIdQuery } from "@/features/diaries/api";
import DiaryTitle from "@/features/diaries/components/DiaryTitle";
import DiaryTwoTabs from "@/features/diaries/components/DiaryTwoTabs";
import {
    getPatientDiaryHomeTab,
    isPatientHomeEditPath,
    patientDiaryHomeBreadcrumbs,
} from "@/features/diaries/utils/patientHomePortal";
import { ensureDiaryReadyLoader } from "@/features/diaries/utils/ensureDiaryReadyLoader";
import { useDiaryCopy } from "@/features/i18n/diaryCopy";
import JourneyProgress from "@/features/onboarding/components/JourneyProgress";
import { useNav } from "@/utils/nav";
import { createFileRoute, Outlet, useRouterState } from "@tanstack/react-router";
import { useMemo } from "react";

const Home = () => {
    const copy = useDiaryCopy();
    const { diaryId } = Route.useParams();
    const { data: diary, isLoading } = useDiaryByIdQuery(diaryId);
    const pathname = useRouterState({ select: (s) => s.location.pathname });
    const isEditRoute = isPatientHomeEditPath(pathname);
    const tab = getPatientDiaryHomeTab(pathname);
    const breadcrumbs = useMemo(
        () =>
            patientDiaryHomeBreadcrumbs(diaryId, tab, {
                diaries: copy.diaries,
                toDo: copy.toDo,
                results: copy.results,
                edit: copy.edit,
            }),
        [copy.diaries, copy.edit, copy.results, copy.toDo, diaryId, tab]
    );

    useNav(breadcrumbs);

    if (isLoading) {
        return null;
    }

    if (!diary) {
        return null;
    }

    return (
        <section className="space-y-4">
            <DiaryTitle diary={diary} editing={isEditRoute} />
            <JourneyProgress diary={diary} />
            {!isEditRoute ? <DiaryTwoTabs diary={diary} /> : null}
            <Outlet />
        </section>
    );
};

export const Route = createFileRoute("/_app/diaries/$diaryId/home")({
    component: Home,
    errorComponent: ({ error, reset }) => <ErrorPage error={error as Error} reset={reset} />,
    loader: async ({ context, params }) => {
        return ensureDiaryReadyLoader(context, params.diaryId);
    },
});
